import React, { useEffect } from "react";
import {
  Button,
  ControlLabel,
  FormGroup,
  FormControl,
  Tooltip,
  OverlayTrigger,
  HelpBlock,
} from "react-bootstrap";
import CancelIcon from "@material-ui/icons/Cancel";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { de } from "date-fns/locale";
import { DatePicker } from "react-nice-dates";
import { Link } from "react-router";
import { orderCreationSlice } from "../orderCreationSlice";
import StoreSearchBar from "../helpers/StoreSearchBar";

export const Store = props => {
  const storeData = useSelector(s => s.slices.orderCreation.storeInfo);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm();

  const dispatch = useDispatch();

  const handleNext = data => {
    dispatch(orderCreationSlice.actions.updateStoreInfo(data));
    props.navigateNextPage();
  };

  const selectedOrderMethod = watch("orderMethod");
  const selectedStore = watch("store");

  useEffect(() => {
    reset(storeData);
  }, [reset, storeData]);

  // Fields
  const contactPerson = register("contactPerson", {
    required: "Bitte wähle einen Ansprechpartner",
    pattern: {
      value: new RegExp(/^[a-zA-Z0-9äöüÄÖÜß&\-.() ]+$/),
      message: "Ungültige Zeichen nicht erlaubt",
    },
  });

  const furtherInformation = register("furtherInformation", {
    pattern: {
      value: new RegExp(
        /^[a-zA-Z0-9äöüÄÖÜß&\-.(),!?\n\r';<>^/\\$€`&%#@*+=[\]" ]+$/,
      ),
      message: "Ungültige Zeichen nicht erlaubt",
    },
  });

  const linkToBilling = (
    <Link to="/faq/billing" target="_blank">
      hier
    </Link>
  );

  return (
    <div className="store-selection-page">
      <h3 className="main-heading">Welcher Markt hat bestellt?</h3>

      <form className="form-wrapper" onSubmit={handleSubmit(handleNext)}>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormGroup validationState={fieldState?.error ? "error" : null}>
              <ControlLabel>Wähle einen Markt aus</ControlLabel>
              {field.value ? (
                <div className="selected-store">
                  {field.value.name}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip">
                        Markt löschen und erneut suchen
                      </Tooltip>
                    }
                  >
                    <CancelIcon
                      cursor="pointer"
                      fontSize="large"
                      onClick={() => field.onChange(null)}
                    />
                  </OverlayTrigger>
                </div>
              ) : (
                <StoreSearchBar onChange={field.onChange} />
              )}
              {fieldState?.error && (
                <HelpBlock>{fieldState?.error.message}</HelpBlock>
              )}
            </FormGroup>
          )}
          name="store"
          rules={{
            required: "Bitte wähle einen Markt",
            validate: {
              checkRegion: field => {
                if (field.region === "Nordbayern-Sachsen-Thüringen") {
                  return (
                    <span>
                      Dieser Markt befindet sich in der Region
                      Nordbayern-Sachsen-Thüringen. Aufträge aus der Region NBST
                      braucht ihr uns nicht zu melden, da hier keine Verrechnung
                      über EDEKA StartHub stattfindet. Mehr Infos{" "}
                      {linkToBilling}.
                    </span>
                  );
                }

                if (field.region === "Hessenring") {
                  return (
                    <span>
                      Dieser Markt befindet sich in der Region Hessenring.
                      Aufträge aus der Region HR braucht ihr uns nicht zu
                      melden, da hier keine Verrechnung über EDEKA StartHub
                      stattfindet. Mehr Infos {linkToBilling}.
                    </span>
                  );
                }

                return true;
              },
            },
          }}
        />

        <FormGroup validationState={errors?.orderMethod ? "error" : null}>
          <ControlLabel>Worüber wurde bestellt</ControlLabel>
          <br />
          <div className="radio-wrapper">
            <label
              className={`order-method-radio ${
                selectedOrderMethod === "telefon" ? "selected" : "not-selected"
              }`}
            >
              <input
                type="radio"
                value="telefon"
                {...register("orderMethod", { required: true })}
              />
              Telefon
            </label>
            <label
              className={`order-method-radio ${
                selectedOrderMethod === "vorort" ? "selected" : "not-selected"
              }`}
            >
              <input
                type="radio"
                value="vorort"
                {...register("orderMethod", { required: true })}
              />
              Vorort
            </label>
            <label
              className={`order-method-radio ${
                selectedOrderMethod === "fax" ? "selected" : "not-selected"
              }`}
            >
              <input
                type="radio"
                value="fax"
                {...register("orderMethod", { required: true })}
              />
              Fax
            </label>
            <label
              className={`order-method-radio ${
                selectedOrderMethod === "andere" ? "selected" : "not-selected"
              }`}
            >
              <input
                type="radio"
                value="andere"
                {...register("orderMethod", { required: true })}
              />
              Andere
            </label>
          </div>
          {errors?.orderMethod && (
            <HelpBlock>Bitte wähle eine Bestellmethode</HelpBlock>
          )}
        </FormGroup>

        <FormGroup validationState={errors?.contactPerson ? "error" : null}>
          <FormControl
            type="text"
            placeholder="Ansprechpartner"
            onChange={contactPerson.onChange}
            inputRef={contactPerson.ref}
            onBlur={contactPerson.onBlur}
            name={contactPerson.name}
          />
          {errors?.contactPerson && (
            <HelpBlock>{errors?.contactPerson.message}</HelpBlock>
          )}
        </FormGroup>

        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormGroup validationState={fieldState?.error ? "error" : null}>
              <DatePicker
                date={field.value}
                onDateChange={field.onChange}
                locale={de}
              >
                {({ inputProps }) => (
                  // Use regular input instead of the FormControl bootstrap wrapper of an input
                  // because the lib does not like it on mobile
                  // Added the form-control className for styling and looks the same
                  <input
                    {...inputProps}
                    placeholder="Bestelldatum"
                    className="form-control"
                  />
                )}
              </DatePicker>
              {fieldState?.error && (
                <HelpBlock>{fieldState.error.message}</HelpBlock>
              )}
            </FormGroup>
          )}
          name="orderDate"
          rules={{
            required: "Bitte wähle ein Bestelldatum",
          }}
        />

        <FormGroup
          validationState={errors?.furtherInformation ? "error" : null}
        >
          <FormControl
            type="text"
            placeholder="Weitere Notizen"
            inputRef={furtherInformation.ref}
            onChange={furtherInformation.onChange}
            onBlur={furtherInformation.onBlur}
            name={furtherInformation.name}
          />
          {errors?.furtherInformation && (
            <HelpBlock>{errors?.furtherInformation.message}</HelpBlock>
          )}
        </FormGroup>
        {!selectedStore && (
          <div className="no-store-message-box">
            Der Markt wird nicht gefunden? <br />
            Bitte sende uns eine kurze Email mit den Marktdetails an&nbsp;
            <a href="mailto:sales@starthub.edeka">sales@starthub.edeka</a>
            . <br /> <br />
            Wir legen den Markt an und geben Dir Bescheid. Vielen Dank!
          </div>
        )}
        <Button className="btn btn-secondary" type="submit">
          Weiter
        </Button>
      </form>
    </div>
  );
};
